package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import coil3.compose.AsyncImage
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * The app's map, rendered by the platform's own map engine where one exists:
 *
 * - **Android** → Google Maps SDK (`maps-compose`), with native clustering and the platform's
 *   dark map style. Mobile map loads on the Maps SDKs carry no charge.
 * - **iOS** → Apple MapKit (`MKMapView` through `UIKitView`), with MapKit's own clustering.
 * - **Web** → [TileMap], the pure-Compose raster map: the Kotlin/JS target draws into a skiko
 *   canvas and cannot host a DOM map SDK, so the hand-rolled map stays the web renderer.
 *
 * The contract is identical on every platform and is the one [TileMap] already had, so screens do
 * not branch on platform. [center]/[zoom] are hoisted: the map moves when they change, and reports
 * user-driven camera moves back through [onCameraChange] once the gesture settles. A
 * [MapPin.selected] pin is never absorbed into a cluster. [attribution] is drawn only where the
 * engine does not draw its own credit (web); native engines show their own legal marks.
 *
 * [interactive] = false turns the map into a static thumbnail (Android lite mode / MapKit with
 * interaction off) — for an embedded preview whose tap opens external directions.
 */
@Composable
expect fun AppMap(
    center: MapPoint,
    zoom: Double,
    onCameraChange: (center: MapPoint, zoom: Double) -> Unit,
    pins: List<MapPin>,
    onPinClick: (String) -> Unit,
    attribution: String,
    modifier: Modifier = Modifier,
    pinContentDescription: String? = null,
    clusterContentDescription: ((Int) -> String)? = null,
    userLocation: MapPoint? = null,
    radiusKm: Double? = null,
    radiusCenter: MapPoint? = null,
    zoomInContentDescription: String? = null,
    zoomOutContentDescription: String? = null,
    interactive: Boolean = true,
    /**
     * A tap on a group of pins that zooming cannot split: every member sits on (almost) the same
     * coordinate, or the camera is already at [MAP_MAX_ZOOM]. Without this the tap is a dead end —
     * co-located listings (a law office and three mortgage agents in one building, ~160 of them on
     * prod) collapse into a bubble that zooms forever and never opens anything (owner report,
     * iPhone, 2026-09-11). Screens answer it by letting the member pick from a list.
     */
    onClusterPinsClick: (ids: List<String>) -> Unit = {},
)

/** Zoom range shared by every renderer, so a hoisted zoom means the same thing on each platform. */
internal const val MAP_MIN_ZOOM = 3.0
internal const val MAP_MAX_ZOOM = 18.0

/** How far tapping a cluster zooms in — enough to break a bubble apart in one step. */
internal const val MAP_CLUSTER_ZOOM_STEP = 2.0

/**
 * Coordinate slack, in degrees (~11 m), within which two pins count as "the same spot": at this
 * separation no amount of zooming pulls them apart, so a cluster of them must be opened as a list
 * instead of zoomed into.
 */
internal const val MAP_SAME_SPOT_DEG = 1e-4

/** True when zooming can no longer separate these pins — they share one spot (see [MAP_SAME_SPOT_DEG]). */
internal fun List<MapPin>.shareOneSpot(): Boolean {
    if (size < 2) return true
    val lats = map { it.lat }
    val lngs = map { it.lng }
    return (lats.max() - lats.min()) < MAP_SAME_SPOT_DEG && (lngs.max() - lngs.min()) < MAP_SAME_SPOT_DEG
}

/** True when the camera is at the tightest zoom the app allows, so a cluster tap cannot zoom further. */
internal fun atMaxZoom(zoom: Double): Boolean = zoom >= MAP_MAX_ZOOM - 0.01

/**
 * The visual of one pin, shared by every renderer so a business or a person looks the same on the
 * raster map, on Google Maps and on MapKit. With [MapPin.label] set it is the person chip (photo or
 * initial on a per-id colour, selected = saffron ring and larger); otherwise the classic pin glyph
 * (selected = saffron and larger). Positioning/anchoring is the caller's job: chips anchor at their
 * centre, pins at the glyph's bottom-centre. [showPhoto] lets a renderer that rasterises markers
 * once (Google Maps) fall back to the initial rather than capture an empty async image.
 */
@Composable
internal fun PinMarkerVisual(pin: MapPin, contentDescription: String?, showPhoto: Boolean = true) {
    if (pin.label != null) {
        val chip = if (pin.selected) AppTheme.sizes.avatarLg else AppTheme.sizes.avatarMd
        Box(
            modifier = Modifier
                .size(chip)
                .background(
                    if (pin.selected) AppTheme.colors.secondary else Color.White,
                    AppTheme.shapes.pill,
                )
                .padding(AppTheme.sizes.strokeSm),
            contentAlignment = Alignment.Center,
        ) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .clip(AppTheme.shapes.pill)
                    .background(avatarColorFor(pin.id)),
                contentAlignment = Alignment.Center,
            ) {
                if (showPhoto && pin.avatarUrl != null) {
                    AsyncImage(
                        model = pin.avatarUrl,
                        contentDescription = contentDescription,
                        contentScale = ContentScale.Crop,
                        modifier = Modifier.fillMaxSize(),
                    )
                } else {
                    Text(
                        text = pin.label.take(1).uppercase(),
                        style = AppTheme.typography.labelMd,
                        color = Color.White,
                    )
                }
            }
        }
    } else {
        AppIcon(
            name = "location_on",
            size = if (pin.selected) AppTheme.sizes.iconHero else AppTheme.sizes.iconLg,
            filled = true,
            tint = if (pin.selected) AppTheme.colors.secondary else AppTheme.colors.primary,
            contentDescription = contentDescription,
        )
    }
}

/** The counted cluster bubble — same on every renderer. */
@Composable
internal fun ClusterBubbleVisual(count: Int) {
    Box(
        modifier = Modifier
            .size(AppTheme.sizes.avatarMd)
            .background(AppTheme.colors.primary, AppTheme.shapes.pill),
        contentAlignment = Alignment.Center,
    ) {
        Text(
            text = count.toString(),
            style = AppTheme.typography.labelMd,
            color = AppTheme.colors.onPrimary,
        )
    }
}

/** The member's own position: white ring, blue core — the convention every map app uses. */
@Composable
internal fun UserLocationDotVisual() {
    Box(
        modifier = Modifier
            .size(AppTheme.sizes.iconSm)
            .background(Color.White, AppTheme.shapes.pill)
            .padding(AppTheme.sizes.strokeSm),
    ) {
        Box(modifier = Modifier.fillMaxSize().background(userLocationDot, AppTheme.shapes.pill))
    }
}

// Overlay colours are fixed light-on-map values — map imagery is light in light theme and the
// engines restyle themselves in dark, so these deliberately do not follow the app theme.
internal val radiusFillColor = Color(0x1A006D77)
internal val radiusStrokeColor = Color(0x80006D77)
internal val userLocationDot = Color(0xFF1A73E8)
